// Makes React's commit phase resilient to DOM mutations performed by
// in-browser translators (Google Translate, Microsoft Translator, etc.).
//
// Translators wrap/replace text nodes after React's last render, so by the
// time React's reconciler tries to `removeChild` or `insertBefore` during
// unmount, the fiber's saved `stateNode` is no longer a direct child of its
// recorded parent. React surfaces this as:
//   - NotFoundError: The object can not be found here. (removeChild)
//   - NotFoundError / HierarchyRequestError (insertBefore)
//
// We soften both calls so the commit phase doesn't explode; React recovers on
// the next render. Reference: https://github.com/facebook/react/issues/11538

let applied = false;

export function applyTranslatorDomSafeguard(): void {
  if (applied || typeof Node === 'undefined') return;
  applied = true;

  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
}
